import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiRefreshCw, FiZap, FiSend } from 'react-icons/fi';
import { useSmartAssistant } from '../hooks/useSmartAssistant';
import UserAssistantPanel from '../components/UserAssistantPanel';
import '../styles/smart-assistant.scss';

const TIME_FRAMES = [
    { key: 'daily', label: 'يومي' },
    { key: 'weekly', label: 'أسبوعي' },
    { key: 'monthly', label: 'شهري' },
];

const SEVERITY_LABELS = {
    critical: 'حرجة',
    high: 'عالية',
    medium: 'متوسطة',
    low: 'منخفضة',
};

const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);

    const toInput = (date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return {
        startDate: toInput(start),
        endDate: toInput(end),
    };
};

const formatDateTime = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleString('ar-LY');
};

export const SmartAssistant = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const {
        insight,
        history,
        isLoading,
        isHistoryLoading,
        isChatLoading,
        generateInsight,
        fetchHistory,
        fetchInsightById,
        sendChatMessage,
    } = useSmartAssistant();

    const [timeframe, setTimeframe] = useState('daily');
    const [range, setRange] = useState(getDefaultRange());
    const [notes, setNotes] = useState('');
    const [chatInput, setChatInput] = useState('');
    const chatEndRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const pageTab = searchParams.get('tab') === 'users' ? 'users' : 'platform';

    useEffect(() => {
        if (!user || !user.token) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (user?.token) {
            fetchHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.token]);

    useEffect(() => {
        setChatInput('');
    }, [insight?._id]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [insight?.chatMessages?.length, isChatLoading]);

    const handleGenerate = () => {
        generateInsight({
            startDate: range.startDate,
            endDate: range.endDate,
            timeframe,
            notes,
        });
    };

    const handleSendChat = (e) => {
        e.preventDefault();
        const message = chatInput.trim();
        if (!message || !insight?._id || isChatLoading) return;
        setChatInput('');
        sendChatMessage(insight._id, message);
    };

    const result = insight?.result;

    return (
        <section className="smart-assistant-page">
            <div className="page-tabs">
                <button type="button" className={pageTab === 'platform' ? 'active' : ''} onClick={() => setSearchParams({})}>تحليل المنصة</button>
                <button type="button" className={pageTab === 'users' ? 'active' : ''} onClick={() => setSearchParams({ tab: 'users' })}>مساعد المستخدمين</button>
            </div>
            {pageTab === 'users' ? <UserAssistantPanel /> : (<>
            <div className="assistant-header">
                <div className="heading">
                    <FiZap />
                    <div>
                        <h2>المساعد الذكي</h2>
                        <p>تحليل بيانات المنصة، مشاكل مع حلول خطوة بخطوة، وخطة تطوير وتسويق ومؤشرات أداء</p>
                    </div>
                </div>

                <div className="notes-row">
                    <label>ملاحظات للتحليل (اختياري)</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder='مثال: "أطلقنا حملة تسويقية الأسبوع الماضي" أو "ركّز على سبب تراجع نشاط السائقين"'
                        rows={2}
                        maxLength={2000}
                    />
                </div>

                <div className="filters">
                    <div className="field">
                        <label>من</label>
                        <input
                            type="date"
                            value={range.startDate}
                            onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label>إلى</label>
                        <input
                            type="date"
                            value={range.endDate}
                            onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label>الإطار الزمني</label>
                        <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                            {TIME_FRAMES.map((item) => (
                                <option key={item.key} value={item.key}>{item.label}</option>
                            ))}
                        </select>
                    </div>
                    <button className="generate-btn" onClick={handleGenerate} disabled={isLoading}>
                        <FiRefreshCw className={isLoading ? 'spinning' : ''} />
                        توليد تحليل
                    </button>
                </div>
            </div>

            <div className="assistant-body">
                <div className="result-panel">
                    {isLoading && (
                        <div className="loading-state">جاري تحليل البيانات... قد يستغرق هذا حتى دقيقة</div>
                    )}

                    {!isLoading && !result && (
                        <div className="empty-state">اختر الفترة الزمنية واضغط "توليد تحليل" للحصول على تحليل ذكي للبيانات.</div>
                    )}

                    {!isLoading && result && (
                        <>
                            {insight.notes && (
                                <div className="result-card notes-used">
                                    <h3>الملاحظات المستخدمة في هذا التحليل</h3>
                                    <p>{insight.notes}</p>
                                </div>
                            )}

                            <div className="result-card">
                                <h3>ملخص عام</h3>
                                <p>{result.summary || 'لا يوجد ملخص متاح'}</p>
                            </div>

                            <div className="result-card">
                                <h3>تقييم النمو</h3>
                                <p>{result.growthAssessment || 'لا يوجد تقييم متاح'}</p>
                            </div>

                            {result.problems?.length > 0 && (
                                <div className="result-card">
                                    <h3>المشاكل والحلول خطوة بخطوة</h3>
                                    <ul className="problem-list">
                                        {result.problems.map((item, index) => (
                                            <li key={index}>
                                                <div className="problem-head">
                                                    <span className={`severity-badge ${item.severity}`}>
                                                        {SEVERITY_LABELS[item.severity] || item.severity}
                                                    </span>
                                                    <p className="title">{item.title}</p>
                                                </div>
                                                {item.steps?.length > 0 && (
                                                    <ol className="steps">
                                                        {item.steps.map((step, stepIndex) => (
                                                            <li key={stepIndex}>{step}</li>
                                                        ))}
                                                    </ol>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.risks?.length > 0 && (
                                <div className="result-card">
                                    <h3>مخاطر ونقاط يجب الانتباه لها</h3>
                                    <ul>
                                        {result.risks.map((risk, index) => (
                                            <li key={index}>{risk}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.incompleteTripReasons?.length > 0 && (
                                <div className="result-card">
                                    <h3>أكثر أسباب عدم اكتمال الرحلات شيوعاً</h3>
                                    <ul className="dropoff-list">
                                        {result.incompleteTripReasons.map((item, index) => (
                                            <li key={index}>
                                                <div className="dropoff-head">
                                                    <p className="stage">{item.stageLabel}</p>
                                                    <span className="stat">{item.count} طلب ({item.percentOfTotal}%)</span>
                                                </div>
                                                {item.likelyReasons?.length > 0 && (
                                                    <ul className="reasons">
                                                        {item.likelyReasons.map((reason, reasonIndex) => (
                                                            <li key={reasonIndex}>{reason}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {item.recommendation && (
                                                    <p className="recommendation">{item.recommendation}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.dispatchStrategy?.length > 0 && (
                                <div className="result-card">
                                    <h3>توصيات استراتيجية إشعار السائقين</h3>
                                    <ul className="dispatch-list">
                                        {result.dispatchStrategy.map((item, index) => (
                                            <li key={index}>
                                                <p className="proposal">{item.proposal}</p>
                                                {item.rationale && <p className="rationale">{item.rationale}</p>}
                                                {item.tradeoff && <p className="tradeoff">المقايضة: {item.tradeoff}</p>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.developmentPlan?.length > 0 && (
                                <div className="result-card">
                                    <h3>خطة تطوير التطبيق</h3>
                                    <div className="phase-grid">
                                        {result.developmentPlan.map((phase, index) => (
                                            <div className="phase-card" key={index}>
                                                <h4>{phase.phase}</h4>
                                                <ul>
                                                    {phase.items?.map((item, itemIndex) => (
                                                        <li key={itemIndex}>{item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {result.marketingPlan?.length > 0 && (
                                <div className="result-card">
                                    <h3>خطة التسويق والنمو</h3>
                                    <div className="phase-grid">
                                        {result.marketingPlan.map((item, index) => (
                                            <div className="phase-card" key={index}>
                                                <h4>{item.strategy}</h4>
                                                <ul>
                                                    {item.actions?.map((action, actionIndex) => (
                                                        <li key={actionIndex}>{action}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {result.kpis?.length > 0 && (
                                <div className="result-card">
                                    <h3>مؤشرات الأداء المقترحة</h3>
                                    <div className="kpi-grid">
                                        {result.kpis.map((kpi, index) => (
                                            <div className="kpi-card" key={index}>
                                                <p className="name">{kpi.name}</p>
                                                <p className="target">{kpi.target}</p>
                                                <p className="timeframe">{kpi.timeframe}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {result.goals?.length > 0 && (
                                <div className="result-card">
                                    <h3>الأهداف القادمة</h3>
                                    <ul className="goal-list">
                                        {result.goals.map((goal, index) => (
                                            <li key={index}>
                                                <span className="goal">{goal.goal}</span>
                                                {goal.timeframe && <span className="timeframe">{goal.timeframe}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.additionalNotes?.length > 0 && (
                                <div className="result-card">
                                    <h3>ملاحظات إضافية</h3>
                                    <ul>
                                        {result.additionalNotes.map((note, index) => (
                                            <li key={index}>{note}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="chat-panel">
                                <h3>اسأل عن هذا التحليل</h3>
                                <div className="chat-messages">
                                    {(!insight.chatMessages || insight.chatMessages.length === 0) && !isChatLoading && (
                                        <p className="muted">اطرح سؤالاً محدداً عن هذا التحليل، مثال: "ما هي أول خطوة يجب أن أنفذها هذا الأسبوع؟"</p>
                                    )}
                                    {insight.chatMessages?.map((msg, index) => (
                                        <div key={index} className={`chat-bubble ${msg.role}`}>
                                            {msg.content}
                                        </div>
                                    ))}
                                    {isChatLoading && (
                                        <div className="chat-bubble assistant loading">جاري الكتابة...</div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>
                                <form className="chat-input-row" onSubmit={handleSendChat}>
                                    <input
                                        type="text"
                                        placeholder="اكتب سؤالك هنا..."
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        disabled={isChatLoading}
                                    />
                                    <button type="submit" disabled={isChatLoading || !chatInput.trim()}>
                                        <FiSend />
                                    </button>
                                </form>
                            </div>
                        </>
                    )}
                </div>

                <aside className="history-panel">
                    <h3>السجل</h3>
                    {isHistoryLoading && <p className="muted">جاري التحميل...</p>}
                    {!isHistoryLoading && history.length === 0 && (
                        <p className="muted">لا يوجد سجل سابق</p>
                    )}
                    <ul>
                        {history.map((item) => (
                            <li
                                key={item._id}
                                className={insight?._id === item._id ? 'active' : ''}
                                onClick={() => fetchInsightById(item._id)}
                            >
                                <span className="date">{formatDateTime(item.createdAt)}</span>
                                <span className="range">
                                    {new Date(item.range.startDate).toLocaleDateString('ar-LY')} - {new Date(item.range.endDate).toLocaleDateString('ar-LY')}
                                </span>
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>
            </>)}
        </section>
    );
};

export default SmartAssistant;
